import "@fontsource/ibm-plex-sans-arabic/400.css";
import "@fontsource/ibm-plex-sans-arabic/500.css";
import "@fontsource/ibm-plex-sans-arabic/600.css";
import "./styles.css";
import { createI18n, setNumberingSystem, type Language } from "@ap/i18n";
import { AuthProvider, type AuthContextValue } from "@ap/ui-kit";
import { StrictMode, useCallback, useState } from "react";
import { createRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { App } from "./App";
import { resolveAuthMode } from "./auth/authMode";
import { decodeJwtPayload, userFromClaims } from "./auth/claims";
import { loadConfig } from "./config";
import { initialLanguage, persistLanguage } from "./lib/language";

async function startAuth(): Promise<AuthContextValue | null> {
  const cfg = await loadConfig();
  setNumberingSystem(cfg.numberingSystem);
  // Dev-server-only bypass for Playwright/mock development (see auth/authMode.ts). In production
  // builds `import.meta.env.DEV` is the literal `false`, so this whole branch is removed.
  if (import.meta.env.DEV && resolveAuthMode(import.meta.env) === "test") {
    const { testUser } = await import("./auth/testAuth");
    const { user, token } = testUser();
    return { user, getToken: () => token, logout: async () => location.reload(), refreshToken: async () => true, mode: "test" };
  }
  const { createUserManager, ensureSignedIn, rememberLoginHint } = await import("./auth/oidc");
  const um = createUserManager(cfg);
  const oidcUser = await ensureSignedIn(um);
  if (!oidcUser) return null; // redirecting to Keycloak
  let token = oidcUser.access_token;
  let current = userFromClaims(decodeJwtPayload(token));
  um.events.addUserLoaded((u) => {
    token = u.access_token;
    current = userFromClaims(decodeJwtPayload(token));
  });
  um.events.addSilentRenewError(() => void um.signinRedirect({ state: location.pathname }));
  return {
    get user() {
      return current;
    },
    getToken: () => token,
    // A token issued before an agent's release lacks its audience (token exchange, ADR-0062).
    refreshToken: async () => {
      try {
        const u = await um.signinSilent();
        if (!u) return false;
        token = u.access_token;
        current = userFromClaims(decodeJwtPayload(token));
        return true;
      } catch {
        return false;
      }
    },
    logout: async () => {
      await um.removeUser();
      await um.signoutRedirect({ id_token_hint: oidcUser.id_token });
    },
    switchUser:
      cfg.demoUsers.length > 0
        ? async (username: string) => {
            rememberLoginHint(username);
            await um.removeUser();
            await um.signoutRedirect({ id_token_hint: oidcUser.id_token });
          }
        : undefined,
    mode: "oidc",
  };
}

async function startMocks(): Promise<void> {
  if (import.meta.env.DEV && import.meta.env.VITE_API_MOCKS === "msw") {
    const { worker } = await import("@ap/api/mocks/browser");
    await worker.start({ onUnhandledRequest: "bypass", quiet: true, serviceWorker: { url: "/mockServiceWorker.js" } });
  }
}

function Root({ auth }: { auth: AuthContextValue }) {
  const [lang, setLang] = useState<Language>(() => initialLanguage(auth.user?.locale));
  const [i18n] = useState(() => createI18n(lang));
  const onLanguage = useCallback(
    (l: Language) => {
      persistLanguage(l);
      void i18n.changeLanguage(l);
      setLang(l);
    },
    [i18n],
  );
  return (
    <StrictMode>
      <I18nextProvider i18n={i18n}>
        <AuthProvider value={auth}>
          <App lang={lang} onLanguage={onLanguage} />
        </AuthProvider>
      </I18nextProvider>
    </StrictMode>
  );
}

async function main() {
  await startMocks();
  const auth = await startAuth();
  if (!auth) return;
  createRoot(document.getElementById("root") as HTMLElement).render(<Root auth={auth} />);
}

void main();
